import { camelize } from '@vimeo/core/libs/camelize';
import { AnyObject, Optional } from '@vimeo/core/types/generic';

import { TFirebaseSnapshot } from '../../types/firebase';
import {
  IChatMessage,
  IChatUser,
  IPoll,
  IPollOption,
  IQnASession,
  TChatHistory,
  TChatParticipants,
  TQnAQuestionReplies,
  TQnAQuestionRepliesRecord,
  TQnAQuestions,
} from '../../types/interaction_tools';

/**
 * Transform object of chat messages to an array of users and messages with appropriate model.
 */
export function transformBulkedChatMessages(chunk: AnyObject = {}): {
  users: TChatParticipants;
  messages: TChatHistory;
} {
  const users: TChatParticipants = {};
  const messages: TChatHistory = {};

  Object.keys(chunk).forEach((id: string) => {
    const data = chunk[id];
    const user = data.user;

    messages[id] = {
      id: id,
      message: data.contents,
      createdAt: data.createdAt,
      senderId: data.user.id,
    };
    users[user.id] = camelize(user);
  });

  return { users, messages };
}

/**
 * Transform single chat message to a separate user and message types.
 */
export function transformChatMessage(
  id: string,
  data: Record<string, any>,
): [IChatMessage, IChatUser] {
  const message: IChatMessage = {
    id: id,
    message: data.contents,
    createdAt: data.createdAt,
    senderId: data.user.id,
  };

  return [message, camelize(data.user)];
}

/**
 * Transform supplied list of questions to an array of question models.
 */
export function transformBulkedQuestions(
  chunk: object = {},
  approved: boolean = false,
  currentUserId?: string,
): TQnAQuestions {
  const questions: TQnAQuestions = {};

  Object.keys(chunk).forEach((id: string) => {
    const data = (chunk as any)[id];

    questions[id] = {
      id,
      approved,
      anonymous: data.anonymous,
      createdAt: data.createdAt,
      archivedAt: data.archivedAt || 0,
      state: data.state,
      text: data.text,
      user: data.user,
      repliesCount: data.repliesCount,
      votesCount: data.votes ? Object.values(data.votes).length : 0,
      isVotedByMe: currentUserId ? Object.keys(data.votes || {}).includes(currentUserId) : false,
    };
  });

  return questions;
}

/**
 * Transform single qna session with active/pending questions.
 */
export function transformQnASession(
  id: string,
  data: AnyObject,
  currentUserId?: string,
): [IQnASession, TQnAQuestions, TQnAQuestions, Optional<string>] {
  const session: IQnASession = {
    id: id,
    createdAt: data.createdAt,
    isQnaModerated: data.isQnaModerated,
    startedAt: data.startedAt,
  };

  return [
    session,
    transformBulkedQuestions(data.questions, true, currentUserId),
    transformBulkedQuestions(data.pendingQuestions),
    data.pinnedQuestionId,
  ];
}

/**
 * Transform supplied list of question replies to an array of question replies models.
 */
export function transformBulkedQuestionReplies(
  chunk: TQnAQuestionRepliesRecord = {},
): TQnAQuestionReplies {
  const questionReplies: TQnAQuestionReplies = {};

  Object.keys(chunk).forEach((id: string) => {
    const data = chunk[id];

    questionReplies[id] = {
      id: id,
      questionId: data.questionId,
      createdAt: data.createdAt,
      text: data.text,
      user: data.user,
    };
  });

  return questionReplies;
}

/**
 * Transform data object from firebase to a poll.
 */
export function transformPoll(id: string, data: AnyObject, currentUserId?: string): IPoll {
  return {
    id: data.id || id,
    state: data.state,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    question: data.question,
    options: data.options
      ? Object.values(data.options as Record<string, AnyObject>).map((it) =>
          transformPollOption(it, currentUserId),
        )
      : [],
  };
}

/**
 * Transform specific option data object from firebase to an appropriate model.
 */
export function transformPollOption(data: AnyObject, currentUserId?: string): IPollOption {
  return {
    id: data.id,
    correct: data.correct,
    votesCount: Object.keys(data.votes || {}).length,
    text: data.text,
    isVotedByMe: currentUserId ? Object.keys(data.votes || {}).includes(currentUserId) : false,
  };
}

export function transformSnapshotToArray<T>(snapshot: TFirebaseSnapshot<T>): Array<T> {
  const container: Array<T> = [];

  if (snapshot !== undefined && snapshot !== null) {
    snapshot.forEach((item) => {
      container.push(item.val());
    });
  }

  return container;
}

export function reBufferizeChatMessages(
  messages: TChatHistory,
  users: TChatParticipants,
  limit: number,
): [TChatHistory, TChatParticipants] {
  const filteredMessages: TChatHistory = {};
  const filteredUsers: TChatParticipants = {};

  Object.values(messages)
    .slice(-limit)
    .forEach((it) => {
      filteredMessages[it.id] = it;
      filteredUsers[it.senderId] = users[it.senderId];
    });

  return [filteredMessages, filteredUsers];
}

// todo: Separate utils file with comparators?
export function graphicsComparator(a: { createdAt: number }, b: { createdAt: number }): number {
  return b.createdAt - a.createdAt;
}

// todo: Separate utils file with comparators?
export function guestsComparator(a: { createdAt: number }, b: { createdAt: number }): number {
  return a.createdAt - b.createdAt;
}
